import {
  DocumentInfo,
  getDocumentsUploadStatusForStatus,
} from "../constants/statusToDocMap/docUploadMap";
import { JCodeDocuments } from "../data-model/medicalPaOrdersModel";
import { DocumentTypesForOrder } from "../enums/documentTypes";
import { getDocumentTypeDisplayName } from "../pages/worklists/singleOrder/documents/documentsTable";

export const checkifAllDocsAreUploaded = (
  authStatus?: string,
  docsFromDB?: JCodeDocuments[],
  jCodeId?: string,
  jCode?: string,
) => {
  const docsForStatus = getDocumentsUploadStatusForStatus(authStatus);

  if (
    docsFromDB &&
    docsFromDB.length > 0 &&
    docsForStatus &&
    docsForStatus.length > 0
  ) {
    return docsForStatus.map((doc) => {
      const docFromDocs = docsFromDB?.find(
        (d) => d?.doc_type === doc?.doc_type && d?.doc_path !== "",
      );

      return {
        ...doc,
        isUploaded: docFromDocs?.doc_path && docFromDocs?.doc_path !== "",
        doc_path: docFromDocs?.doc_path || "",
        doc_id: docFromDocs?.id || jCodeId || "",
        doc_title: docFromDocs?.doc_title || "",
        j_code: jCode || "",
        uploaded_text: docFromDocs?.doc_title || "",
      };
    });
  }

  return docsForStatus;
};

/**
 * Checks the upload status of the `auth_letter` document for a given J-Code.
 *
 * Unlike {@link checkifAllDocsAreUploaded}, this function:
 * - Filters the status-based document list to only the `auth_letter` type
 * - Returns the enriched auth letter document with upload metadata
 *
 * @param authStatus - Current authorization status of the J-Code
 * @param docsFromDB - List of documents associated with the J-Code from the database
 * @param jCodeId - The J-Code record identifier (used as fallback for `doc_id`)
 * @param jCode - The J-Code value
 *
 * @returns An array containing a single auth letter document object with upload details,
 *          or an empty array if the auth letter is not expected for the given status.
 */
export const getAuthLetterDocUploadStatus = (
  authStatus?: string,
  docsFromDB?: JCodeDocuments[],
  jCodeId?: string,
  jCode?: string,
) => {
  const docsForStatus = getDocumentsUploadStatusForStatus(authStatus);

  const authLetterDoc = docsForStatus?.find(
    (doc) => doc?.doc_type === DocumentTypesForOrder.AUTH_LETTER,
  );

  if (!authLetterDoc) {
    return [];
  }

  const matchingDbDoc = docsFromDB
    ?.filter(
      (d) =>
        d?.doc_type === DocumentTypesForOrder.AUTH_LETTER && d?.doc_path !== "",
    )
    .sort((a, b) => {
      const aDate = a?.doc_upload_date
        ? Date.parse(a.doc_upload_date)
        : Number.NEGATIVE_INFINITY;
      const bDate = b?.doc_upload_date
        ? Date.parse(b.doc_upload_date)
        : Number.NEGATIVE_INFINITY;
      return bDate - aDate;
    })[0];

  return [
    {
      ...authLetterDoc,
      isUploaded: !!matchingDbDoc?.doc_path && matchingDbDoc?.doc_path !== "",
      doc_path: matchingDbDoc?.doc_path || "",
      doc_id: matchingDbDoc?.id || jCodeId || "",
      doc_title: matchingDbDoc?.doc_title || "",
      j_code: jCode || "",
      uploaded_text: matchingDbDoc?.doc_title || "",
    },
  ];
};

export const getDocsNotBelongingToCurrentAuthStatus = (
  authStatus: string,
  docsFromDB: JCodeDocuments[],
  jCode?: string,
  jCodeId?: string,
): DocumentInfo[] => {
  const docsForStatus = getDocumentsUploadStatusForStatus(authStatus);

  const docsNotBelongingToCurrentAuthStatus = docsFromDB.filter(
    (doc) =>
      doc?.doc_path !== "" &&
      !docsForStatus.some((d) => d?.doc_type === doc?.doc_type),
  );

  return docsNotBelongingToCurrentAuthStatus.map((doc) => ({
    isUploaded: doc?.doc_path && doc?.doc_path !== "" ? true : false,
    doc_type: doc?.doc_type || "",
    doc_upload_date: doc?.doc_upload_date || "",
    doc_upload_by: doc?.doc_upload_by || "",
    doc_path: doc?.doc_path || "",
    doc_id: doc?.id || jCodeId || "",
    doc_title: doc?.doc_title || "",
    uploaded_text: doc?.doc_title || "",
    text_to_display: getDocumentTypeDisplayName(doc?.doc_type || ""),
    j_code: jCode || "",
  }));
};
